
import React, { useState } from "react";
import { FileText, Download, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";

interface MessageContentProps {
  message: string;
  attachment_path?: string | null;
}

const MessageContent: React.FC<MessageContentProps> = ({
  message,
  attachment_path,
}) => {
  // Check if attachment is a PDF based on the file extension
  const isPdf = attachment_path?.toLowerCase().endsWith('.pdf');
  const [showFullImage, setShowFullImage] = useState(false);
  
  // Handler for downloading the file
  const handleDownload = () => {
    if (attachment_path) {
      // Create a temporary link element
      const a = document.createElement('a');
      a.href = attachment_path;
      a.download = attachment_path.split('/').pop() || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  // Handler for previewing images in a larger overlay
  const handleToggleFullImage = () => {
    setShowFullImage(!showFullImage);
  };

  return (
    <div>
      <div className="whitespace-pre-wrap">{message}</div>
      {attachment_path && (
        <div className="mt-2">
          {isPdf ? (
            <div className="border border-gray-200 rounded-md p-3 bg-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-6 w-6 text-blue-600" />
                <a 
                  href={attachment_path} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-blue-600 hover:underline"
                >
                  PDF bestand bekijken
                </a>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDownload}
                className="ml-2"
              >
                <Download className="h-4 w-4 mr-1" />
                Download
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="relative bg-gray-50 p-2 rounded-md border border-gray-200">
                <img
                  src={attachment_path}
                  alt="Bijlage"
                  className="max-w-full max-h-[300px] rounded-md object-contain mx-auto"
                  onClick={() => window.open(attachment_path, '_blank')}
                />
                <div className="absolute top-2 right-2 flex gap-2">
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={() => window.open(attachment_path, '_blank')}
                    className="bg-white bg-opacity-80 hover:bg-opacity-100"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Bekijken
                  </Button>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDownload}
              >
                <Download className="h-4 w-4 mr-1" />
                Download afbeelding
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Full image overlay */}
      {showFullImage && !isPdf && attachment_path && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={handleToggleFullImage}
        >
          <div className="relative max-w-4xl max-h-screen p-2">
            <img 
              src={attachment_path} 
              alt="Volledige afbeelding" 
              className="max-w-full max-h-full object-contain"
            />
            <Button 
              className="absolute top-2 right-2"
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDownload();
              }}
            >
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageContent;
